import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  History, 
  Search, 
  Download, 
  Filter,
  User,
  Calendar,
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  Plus,
  Eye,
  Mail,
  Check,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: '',
    user_id: '',
    action: '',
    search: ''
  });
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0
  });

  // Entity types for filter
  const entityTypes = [
    { value: '', label: 'Tutti i Tipi' },
    { value: 'lead', label: 'Lead' },
    { value: 'estimate', label: 'Preventivo' },
    { value: 'contract', label: 'Contratto' },
    { value: 'project', label: 'Progetto' },
    { value: 'extra', label: 'Extra' },
    { value: 'furniture_quote', label: 'Preventivo Arredi' },
    { value: 'user', label: 'Utente' },
    { value: 'settings', label: 'Impostazioni' },
    { value: 'appointment', label: 'Appuntamento' }
  ];

  // Action types for filter
  const actionTypes = [
    { value: '', label: 'Tutte le Azioni' },
    { value: 'create', label: 'Creazione' },
    { value: 'update', label: 'Modifica' },
    { value: 'delete', label: 'Eliminazione' },
    { value: 'view', label: 'Visualizzazione' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'validate', label: 'Validazione' },
    { value: 'generate_pdf', label: 'Genera PDF' },
    { value: 'send_email', label: 'Invio Email' }
  ];

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      params.append('limit', pagination.limit.toString());
      
      const response = await fetch(`${API_URL}/api/audit-logs?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        let data = await response.json();
        
        // Client-side filtering for action and search
        if (filters.action) {
          data = data.filter(log => log.action?.toLowerCase().includes(filters.action.toLowerCase()));
        }
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          data = data.filter(log => 
            log.entity_id?.toLowerCase().includes(searchLower) ||
            log.entity_type?.toLowerCase().includes(searchLower) ||
            log.action?.toLowerCase().includes(searchLower) ||
            JSON.stringify(log.details || {}).toLowerCase().includes(searchLower)
          );
        }
        
        setLogs(data);
        setPagination(prev => ({ ...prev, total: data.length }));
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionIcon = (action) => {
    switch (action?.toLowerCase()) {
      case 'create': return <Plus className="w-4 h-4 text-green-500" />;
      case 'update': return <Edit className="w-4 h-4 text-blue-500" />;
      case 'delete': return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'view': return <Eye className="w-4 h-4 text-gray-500" />;
      case 'login': return <User className="w-4 h-4 text-green-500" />;
      case 'logout': return <User className="w-4 h-4 text-gray-500" />;
      case 'validate': return <Check className="w-4 h-4 text-green-500" />;
      case 'generate_pdf': return <FileText className="w-4 h-4 text-purple-500" />;
      case 'send_email': return <Mail className="w-4 h-4 text-blue-500" />;
      default: return <History className="w-4 h-4 text-gray-400" />;
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      'create': 'bg-green-100 text-green-800',
      'update': 'bg-blue-100 text-blue-800',
      'delete': 'bg-red-100 text-red-800',
      'view': 'bg-gray-100 text-gray-800',
      'login': 'bg-emerald-100 text-emerald-800',
      'logout': 'bg-slate-100 text-slate-800',
      'validate': 'bg-green-100 text-green-800',
      'generate_pdf': 'bg-purple-100 text-purple-800',
      'send_email': 'bg-blue-100 text-blue-800'
    };
    return colors[action?.toLowerCase()] || 'bg-gray-100 text-gray-600';
  };

  const getEntityLabel = (type) => {
    const labels = {
      'lead': 'Lead',
      'estimate': 'Preventivo',
      'contract': 'Contratto',
      'project': 'Progetto',
      'extra': 'Extra',
      'furniture_quote': 'Arredi',
      'user': 'Utente',
      'settings': 'Impostazioni',
      'appointment': 'Appuntamento'
    };
    return labels[type?.toLowerCase()] || type;
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
    }
    return userId?.substring(0, 8) || 'Sistema';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const exportToCSV = () => {
    const headers = ['Data', 'Utente', 'Azione', 'Tipo Entità', 'ID Entità', 'Dettagli'];
    const rows = logs.map(log => [
      formatDate(log.timestamp || log.created_at),
      getUserName(log.user_id),
      log.action,
      log.entity_type,
      log.entity_id,
      JSON.stringify(log.details || {})
    ]);
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const clearFilters = () => {
    setFilters({
      entity_type: '',
      user_id: '',
      action: '',
      search: ''
    });
  };

  // Pagination
  const startIndex = (pagination.page - 1) * pagination.limit;
  const endIndex = startIndex + pagination.limit;
  const paginatedLogs = logs.slice(startIndex, endIndex);
  const totalPages = Math.ceil(logs.length / pagination.limit);

  return (
    <div className="space-y-6 p-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-500">Cronologia di tutte le operazioni sul sistema</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchLogs}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button 
            onClick={exportToCSV}
            disabled={logs.length === 0}
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Esporta CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Cerca in ID, tipo, azione, dettagli..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Entity Type */}
            <Select
              value={filters.entity_type || 'all'}
              onValueChange={(value) => setFilters(prev => ({ ...prev, entity_type: value === 'all' ? '' : value }))}
            >
              <SelectTrigger data-testid="entity-type-filter">
                <SelectValue placeholder="Tipo Entità" />
              </SelectTrigger>
              <SelectContent>
                {entityTypes.map(type => (
                  <SelectItem key={type.value || 'all'} value={type.value || 'all'}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Action Type */}
            <Select
              value={filters.action || 'all'}
              onValueChange={(value) => setFilters(prev => ({ ...prev, action: value === 'all' ? '' : value }))}
            >
              <SelectTrigger data-testid="action-filter">
                <SelectValue placeholder="Azione" />
              </SelectTrigger>
              <SelectContent>
                {actionTypes.map(action => (
                  <SelectItem key={action.value || 'all'} value={action.value || 'all'}>
                    {action.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* User */}
            <Select
              value={filters.user_id || 'all'}
              onValueChange={(value) => setFilters(prev => ({ ...prev, user_id: value === 'all' ? '' : value }))}
            >
              <SelectTrigger data-testid="user-filter">
                <SelectValue placeholder="Utente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli Utenti</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clear Filters */}
          {(filters.entity_type || filters.action || filters.user_id || filters.search) && (
            <div className="mt-4">
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Cancella filtri
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          {logs.length} risultati trovati
          {(filters.entity_type || filters.action || filters.user_id || filters.search) && ' (filtrati)'}
        </span>
        <span>
          Pagina {pagination.page} di {totalPages || 1}
        </span>
      </div>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <History className="w-12 h-12 mb-4 text-gray-300" />
              <p>Nessun log trovato</p>
              <p className="text-sm">Prova a modificare i filtri</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Data/Ora
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Utente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Azione
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Entità
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Dettagli
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedLogs.map((log, index) => (
                    <tr key={log.id || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDate(log.timestamp || log.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-orange-600" />
                          </div>
                          <span className="font-medium text-gray-900">
                            {getUserName(log.user_id)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${getActionBadge(log.action)} flex items-center gap-1 w-fit`}>
                          {getActionIcon(log.action)}
                          {log.action}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {getEntityLabel(log.entity_type)}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-500">
                        {log.entity_id?.substring(0, 8) || '-'}...
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <span title={JSON.stringify(log.details, null, 2)}>
                            {Object.entries(log.details).slice(0, 2).map(([k, v]) => (
                              <span key={k} className="mr-2">
                                <span className="text-gray-400">{k}:</span> {String(v).substring(0, 20)}
                              </span>
                            ))}
                            {Object.keys(log.details).length > 2 && '...'}
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page <= 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          {[...Array(Math.min(5, totalPages))].map((_, i) => {
            const pageNum = pagination.page <= 3 
              ? i + 1 
              : pagination.page + i - 2;
            if (pageNum > totalPages || pageNum < 1) return null;
            return (
              <Button
                key={pageNum}
                variant={pagination.page === pageNum ? "default" : "outline"}
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
              >
                {pageNum}
              </Button>
            );
          })}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page >= totalPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
